import React, { useEffect, useId, useRef } from 'react';

const DRIFT_DUR = '8s';
const INTERVAL_MS = 8000;

const randPct = () => `${15 + Math.floor(Math.random() * 70)}%`;

/** Generate a random starting focal point. */
const randFocal = () => ({ cx: randPct(), cy: randPct() });

/**
 * Renders an absolutely-positioned SVG that fills its parent with drifting
 * radial gradients (main-100 → main-300).  Same animation style as
 * DynamicInvoiceIcon but for rectangular backgrounds.
 *
 * Each instance randomises its initial focal points so multiple instances
 * on the same page look different from the start.
 */
export const AnimatedGradientBg = () => {
  const id = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const initialsRef = useRef([randFocal(), randFocal()]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;

    const triggerAnimation = () => {
      svg.querySelectorAll('animate').forEach((anim) => {
        const cur =
          anim.getAttribute('data-cur') ??
          anim.getAttribute('data-start') ??
          '50%';
        const next = randPct();
        anim.setAttribute('values', `${cur};${next}`);
        anim.setAttribute('data-cur', next);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        anim.beginElement?.();
      });
    };

    triggerAnimation();
    const interval = setInterval(triggerAnimation, INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const colors = ['rgb(var(--colour-main-100))', 'rgb(var(--colour-main-200))'];

  return (
    <svg
      ref={svgRef}
      className="absolute inset-0 w-full h-full pointer-events-none group-hover:opacity-0 transition-opacity"
      preserveAspectRatio="none"
    >
      <defs>
        {colors.map((color, i) => {
          const fp = initialsRef.current[i];
          return (
            <radialGradient
              key={`${id}-rg-${i}`}
              id={`${id}-rg-${i}`}
              cx={fp.cx}
              cy={fp.cy}
              r="70%"
            >
              <stop offset="0%" stopColor={color} stopOpacity={1} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
              <animate
                attributeName="cx"
                data-start={fp.cx}
                data-cur={fp.cx}
                values={`${fp.cx};${fp.cx}`}
                dur={DRIFT_DUR}
                begin="indefinite"
                fill="freeze"
              />
              <animate
                attributeName="cy"
                data-start={fp.cy}
                data-cur={fp.cy}
                values={`${fp.cy};${fp.cy}`}
                dur={DRIFT_DUR}
                begin="indefinite"
                fill="freeze"
              />
            </radialGradient>
          );
        })}
      </defs>
      {/* Base fill */}
      <rect width="100%" height="100%" fill={colors[0]} />
      {/* Overlay gradient */}
      <rect width="100%" height="100%" fill={`url(#${id}-rg-1)`} />
    </svg>
  );
};
